import { Component, EventEmitter, Host, HostBinding, Inject, Input, Optional, Output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { simpleValueValidation } from '@miq/utils';
import { BaseColumnControlValidatorPlugin } from '@controls/core/base-column-validator-plugin.service';
import { AbstractFormRegisterableComponentTemplate } from '@controls/core/form-registerable-template.component';
import { InputControlBuilder } from '@controls/core/input-control-builder.service';
import { ControlListenerPlugin, ControlListenerPluginToken } from '@controls/model/control-listener-plugin';
import { ControlValidatorPlugin, ControlValidatorPluginToken } from '@controls/model/control-validator-plugin';
import { FormControlMaker, FormControlMakerToken } from '@controls/model/form-control-maker';
import { Column } from '@miq/catalog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataObject } from '@shared/models/data-object.model';
import { getLovValueKey } from '@shared/utils/helpers/lov-datakey-factory';
import { filter, map } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'miq-input',
  templateUrl: '../../templates/input.component.html',
  providers: [
    // control builder
    { provide: FormControlMakerToken, useExisting: InputControlBuilder },
    // validator
    { provide: ControlValidatorPluginToken, useExisting: BaseColumnControlValidatorPlugin },
  ],
  standalone: false,
})
export class InputComponent extends AbstractFormRegisterableComponentTemplate<Column> {
  @Input() showLabel = true;
  @Output() configChange = new EventEmitter<Column>();
  relatedControls: { [key: string]: AbstractControl } = {};

  @HostBinding('class.ng-warning') warningIndicator = false;

  constructor(
    // control builder
    @Inject(FormControlMakerToken) controlBuilder: FormControlMaker,
    // validator
    @Host() @Inject(ControlValidatorPluginToken) controlValidatorPlugin: ControlValidatorPlugin<Column>,
    // listeners
    @Host()
    // optional services
    @Optional()
    @Inject(ControlListenerPluginToken)
    listenerPlugins: Array<ControlListenerPlugin<Column>>
  ) {
    super(controlBuilder, controlValidatorPlugin, listenerPlugins);
  }

  protected _onDataResolved(data: DataObject) {
    this.registerControl(this.config, this.form, data);

    for (const relCol of this.config.getRelatedColumns()) {
      const control = this._buildControl(relCol, this.form, data);
      const validators = relCol.validatorFns();
      if (validators != null) {
        control.setValidators(relCol.validatorFns());
      }
      // set up listeners (if any)
      this.setUpListeners(relCol, control, this.form);
      this.relatedControls[relCol.columnId] = control;
    }

    if ((this.config.field || '').toLowerCase().includes('property name')) {
      this.warningIndicator = true;
    }

    this.afterRegisterControl(data);
  }

  protected afterRegisterControl(_data: DataObject) {
    super.afterRegisterControl();

    const relatedControlConfigs = this.config
      .getRelatedColumns()
      .filter(col => this.relatedControls[col.columnId] && col.lookupCategory != null)
      .map(col => ({ config: col, control: this.relatedControls[col.columnId] }));

    relatedControlConfigs.forEach(({ control, config }) => {
      this._setUomAutoPopulating(this.control, control, config);
    });

    this._setWarningHighlighting();
  }

  protected _setWarningHighlighting() {
    this.control.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.warningIndicator = this._hasControlWarnings(this.control)));
  }

  protected _hasControlWarnings = ctrl => Object.keys((ctrl as any)?.warnings ?? {}).length > 0;
  /**
   * Sets up the listeners to enable the behavior of selecting first option as soon as value is typed in if no option has been previously selected
   * @param valueControl
   * @param uomControl
   */

  protected _setUomAutoPopulating = (
    valueControl: AbstractControl,
    uomControl: AbstractControl,
    targetColumn?: Column
  ): void => {
    // we don't need to generate a subscription if we don't have values to use
    valueControl.valueChanges
      .pipe(
        untilDestroyed(this),
        map(value => {
          targetColumn = targetColumn || this.config.unit;
          const dataKey = getLovValueKey(targetColumn);
          const firstOption = this._getFirstOption(targetColumn);
          return { value, firstOption, dataKey };
        }),
        filter(({ firstOption }) => firstOption != null)
      )
      .subscribe(({ value, firstOption, dataKey }) => {
        // If the initial value and current uom are null and the updated value is not null, auto-populate with the first IOption
        if (simpleValueValidation(value) && !simpleValueValidation(uomControl.value)) {
          this._patchControlValueSilently(uomControl, firstOption[dataKey]);
          // If the initial value and current uom are not null and the updated value is null, reset UOM
        } else if (!simpleValueValidation(value) && simpleValueValidation(uomControl.value)) {
          this._patchControlValueSilently(uomControl, null);
        }
      });
  };

  protected _getFirstOption(config: Column): DataObject {
    const [firstOption = null] = config.values ?? [];
    return firstOption;
  }

  protected _patchControlValueSilently(control: AbstractControl, value: any): void {
    const onlySelf = { onlySelf: true };
    control.patchValue(value, onlySelf);
    // mark as dirty if not already marked
    if (!control.dirty) control.markAsDirty(onlySelf);
  }
}
